import axios from 'axios'
import React, { useState, useContext, useEffect, useMemo } from 'react'
import Button from '../../../button/Button'
import { baseApiURL } from 'src/utils/utils'
import Input from '../../../input/Input'
import { IState, IUser } from 'src/Interface'
import { StateContext } from '../../../../AppContext'
import styles from './add-account.module.scss'
import MultiValueInput from 'src/components/multiValueInput/MultiValueInput'
import { toast } from 'react-toastify'
import ToggleSwitch from 'src/components/toggleSwitch/ToggleSwitch'
import { validString } from 'src/utils/utils'
import { DispatchContext } from 'src/AppContext'
import useCancellablePromise from 'src/utils/useCancellablePromise'

interface IError {
  accountName: boolean
  code: boolean
}

interface Props {
  close: () => void
  companyId: number
}



const AddAccount: React.FunctionComponent<Props> = React.memo(function AddAccount({ close, companyId }: Props) {
  const state: IState = useContext(StateContext)
  const [isLoading, setIsLoading] = useState(false)
  const [users, setUsers] = useState<Array<IUser>>([])
  const { cancellablePromise } = useCancellablePromise()
  const [selectedUsers, updateSelectedUsers] = useState([])
  const accessToken = useMemo(() => state.user.accessToken, [state.user.accessToken])
  const dispatch = useContext(DispatchContext)
  const validId = /^\d{1,6}$/

  const [values, setValues] = useState<any>(JSON.parse(window.sessionStorage.getItem('accounts')) || {
    accountName: '',
    code: '',
    userIds: []
  })

  const [isDefaultAccount, setDefaultAccount] = useState(false)

  const [errors, setErrors] = useState<IError>({
    accountName: false,
    code: false,
  })

  const [validationErrors, setValidationErrors] = useState<IError>({
    accountName: false,
    code: false,
  })

  useEffect(() => {
    if (accessToken && companyId) {
      cancellablePromise(axios(`${baseApiURL()}/users${`?companyId=${companyId}`}`, {
        headers: {
          Authorization:
            `Bearer ${accessToken}`
        }
      }).then((result) => {
        setUsers(result.data)
      }).catch((err) => {
        toast(err.response.data.message)
      }))
    }
  }, [accessToken, cancellablePromise, companyId])

  const handleInputChange = (fieldName: string) => (value: string) => {
    window.sessionStorage.setItem('accounts', JSON.stringify({
      ...values,
      [fieldName]: value
    }))
    setValues({
      ...values,
      [fieldName]: value
    })
  }

  const resetError = (fieldName: string) => {
    setErrors({
      ...errors,
      [fieldName]: false
    })
    setValidationErrors({
      ...validationErrors,
      [fieldName]: false
    })
  }

  const handleValidation = (regexp: RegExp, fieldName: string) => (value: string) => {
    let error = false
    if (value && regexp && !regexp.test(value || '')) {
      error = true
    }

    setValidationErrors({
      ...validationErrors,
      [fieldName]: error
    })
  }

  const createAccount = () => {
    setIsLoading(true)
    axios.post(`${baseApiURL()}/companies/${companyId}/accounts`, {
      name: values.accountName,
      code: values.code,
      defaultAccount: isDefaultAccount,
      userIds: selectedUsers.map(user => user.id)
    }, {
      headers: {
        Authorization: `Bearer ${state.user.accessToken}`
      }
    }).then((results) => {
      window.sessionStorage.removeItem('accounts')
      return results.data.accountId
    }).then((accountId) => {
      return axios(`${baseApiURL()}/companies/${companyId}/accounts/${accountId}`, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      })
    }).then((result) => {
      dispatch({ type: 'SET_ACCOUNTS', accounts: [result.data, ...state.accounts] })
      setIsLoading(false)
      toast('Konto oprettet')
      close()
    }).catch(error => {
      setIsLoading(false)
      if (error.response.data.message.startsWith('Key (company_id, name)=')) {
        setErrors({
          ...error,
          accountName: true
        })
      } else if (error.response.data.message.startsWith('Key (company_id, code)=')) {
        setErrors({
          ...error,
          code: true
        })
      } else {
        if (error.response) {
          toast(error.response.data.message)
        } else {
          toast('Der gik desværre noget galt. Prøv igen om lidt')
        }
      }
    })
  }

  return (
    <div className={styles.addAccount}>
      <header className={styles.header}>
        <h4>Opret konto</h4>
      </header>
      <div className={styles.container}>
        <div className={styles.section}>
          <div className='tw-mb-3'>
            <Input
              label='Konto navn *'
              type='text'
              error={errors.accountName || validationErrors.accountName}
              resetError={() => resetError('accountName')}
              handleValidation={() => handleValidation(validString, 'accountName')}
              value={values.accountName}
              handleInputChange={() => handleInputChange('accountName')}
              name='Konto navn'
              placeholder='Navn'
            />
            {validationErrors.accountName && <p className='errorMsg'>Kontonavnet bør være mellem 2 og 32 karakterer</p>}
            {errors.accountName && <p className='errorMsg'>Kontonavnet eksisterer allerede</p>}
          </div>
          <div className='tw-mb-3'>
            <Input
              label='Konto code *'
              type='text'
              value={values.code}
              error={errors.code || validationErrors.code}
              resetError={() => resetError('code')}
              handleValidation={() => handleValidation(validId, 'code')}
              handleInputChange={() => handleInputChange('code')}
              name='code'
              placeholder='Konto code'
            />
            {validationErrors.code && <p className='errorMsg'>bør kun bestå af tal og være mellem 1 og 6 karakterer.</p>}
            {errors.code && <p className='errorMsg'>Konto koden eksisterer allerede</p>}
          </div>
          <MultiValueInput label='Medarbejdere' updateList={updateSelectedUsers} placeholder='Tilføj medarbejdere”' name='users' list={users} />
          <div className={styles.defaultAccount}>
            <ToggleSwitch checked={isDefaultAccount} label='Standard konto' onChange={setDefaultAccount} />
          </div>
        </div>
      </div>
      <footer className={styles.footer}>
        <Button type='secondary' label='Annuller' handleClick={close} />
        <Button loading={isLoading} type='primary' label='Opret konto' handleClick={createAccount} disabled={(!(values.code && values.accountName) || !!Object.values(validationErrors).filter(error => error).length)} />
      </footer>
    </div>
  )
})

export default AddAccount
